import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { AppLayout } from '@/components/layout/AppLayout';
import { Breadcrumb } from '@/components/layout/Breadcrumb';
import { mockMeetings, mockClients, mockProjects, mockTasks } from '@/mocks/data';
import { Link } from 'react-router-dom';
import type { ActionItem } from '@/types';

interface MeetingDetailClientProps {
  meetingId: string;
}

export function MeetingDetailClient({ meetingId }: MeetingDetailClientProps) {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<'summary' | 'transcript' | 'actions'>('summary');

  const meeting = mockMeetings.find((m) => m.id === meetingId);

  if (!meeting) {
    return (
      <AppLayout>
        <div className="border border-border rounded-lg p-4 text-center py-12">
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Meeting not found</h2>
          <p className="text-gray-600 mb-4">
            The meeting you&apos;re looking for doesn&apos;t exist.
          </p>
          <Link
            to="/meetings"
            className="px-4 py-2 bg-flowky-primary text-white rounded-lg hover:bg-flowky-primary/90 transition-colors"
          >
            Back to Meetings
          </Link>
        </div>
      </AppLayout>
    );
  }

  const client = mockClients.find((c) => c.id === meeting.clientId);
  const project = mockProjects.find((p) => p.id === meeting.projectId);
  const linkedTasks = mockTasks.filter((t) => meeting.linkedTasks?.includes(t.id));

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
    });
  };

  const handleActionToggle = (actionId: string) => {
    console.log('Toggle action:', actionId);
    // In real app, this would update the action item status
  };

  const handleCreateTask = (actionItem: ActionItem) => {
    console.log('Create task from action:', actionItem);
    const params = new URLSearchParams({
      title: actionItem.task,
      meetingId: meeting.id,
      ...(meeting.clientId && { clientId: meeting.clientId }),
      ...(meeting.projectId && { projectId: meeting.projectId }),
      ...(actionItem.dueDate && { deadline: actionItem.dueDate }),
    });
    navigate(`/tasks/new?${params.toString()}`);
  };

  const pendingActions = meeting.actionItems?.filter((a) => !a.completed) || [];
  const completedActions = meeting.actionItems?.filter((a) => a.completed) || [];

  return (
    <AppLayout>
      <Breadcrumb
        items={[{ label: 'Meetings', href: '/meetings' }, { label: 'Meeting Details' }]}
      />

      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="border border-border rounded-lg p-4 mb-6">
          <div className="flex items-start justify-between">
            <div className="flex-1">
              <div className="flex items-center gap-3 mb-2">
                <h1 className="text-2xl font-bold text-gray-900">
                  Meeting with {client?.name || 'Unknown Client'}
                </h1>
                <div className="flex items-center gap-2">
                  {pendingActions.length > 0 && (
                    <span className="px-2 py-1 text-xs bg-orange-100 text-orange-700 rounded-full">
                      {pendingActions.length} pending action{pendingActions.length !== 1 ? 's' : ''}
                    </span>
                  )}
                  {project && (
                    <span className="px-2 py-1 text-xs bg-blue-100 text-blue-700 rounded-full">
                      {project.name}
                    </span>
                  )}
                </div>
              </div>

              <div className="space-y-1 text-sm text-gray-600">
                <p>📅 {formatDate(meeting.date)}</p>
                <p>👥 {meeting.participants.join(', ')}</p>
                {linkedTasks.length > 0 && (
                  <p>
                    🔗 Linked to {linkedTasks.length} task{linkedTasks.length !== 1 ? 's' : ''}:{' '}
                    {linkedTasks.map((task, index) => (
                      <span key={task.id}>
                        <Link
                          to={`/tasks/${task.id}`}
                          className="text-flowky-secondary hover:text-blue-700"
                        >
                          {task.title}
                        </Link>
                        {index < linkedTasks.length - 1 && ', '}
                      </span>
                    ))}
                  </p>
                )}
              </div>
            </div>

            <div className="flex gap-2">
              <button className="p-2 border border-gray-300 rounded-lg hover:bg-white/30 transition-colors">
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
                  />
                </svg>
              </button>
              <button className="p-2 border border-gray-300 rounded-lg hover:bg-white/30 transition-colors">
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.367 2.684 3 3 0 00-5.367-2.684z"
                  />
                </svg>
              </button>
            </div>
          </div>
        </div>

        {/* Navigation Tabs */}
        <div className="border border-border rounded-lg p-4 mb-6">
          <div className="border-b border-white/30">
            <nav className="-mb-px flex space-x-8">
              <button
                onClick={() => setActiveTab('summary')}
                className={`py-2 px-1 border-b-2 font-medium text-sm ${
                  activeTab === 'summary'
                    ? 'border-flowky-primary text-flowky-primary'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                📋 Summary
              </button>
              <button
                onClick={() => setActiveTab('transcript')}
                className={`py-2 px-1 border-b-2 font-medium text-sm ${
                  activeTab === 'transcript'
                    ? 'border-flowky-primary text-flowky-primary'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                📝 Transcript
              </button>
              <button
                onClick={() => setActiveTab('actions')}
                className={`py-2 px-1 border-b-2 font-medium text-sm ${
                  activeTab === 'actions'
                    ? 'border-flowky-primary text-flowky-primary'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                ✅ Action Items ({pendingActions.length})
              </button>
            </nav>
          </div>
        </div>

        {/* Tab Content */}
        {activeTab === 'summary' && (
          <div className="border border-border rounded-lg p-4">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Meeting Summary</h3>
            {meeting.summary ? (
              <div className="prose max-w-none">
                <p className="text-gray-700 leading-relaxed whitespace-pre-line">
                  {meeting.summary}
                </p>
              </div>
            ) : (
              <div className="text-center py-8">
                <svg
                  className="w-12 h-12 mx-auto text-gray-400 mb-3"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                  />
                </svg>
                <p className="text-gray-500">No summary available for this meeting</p>
                <button className="mt-2 text-flowky-secondary hover:text-blue-700 text-sm font-medium">
                  Add Summary
                </button>
              </div>
            )}
          </div>
        )}

        {activeTab === 'transcript' && (
          <div className="border border-border rounded-lg p-4">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Meeting Transcript</h3>
            {meeting.transcript ? (
              <div className="prose max-w-none">
                <div className="bg-gray-50 rounded-lg p-4 font-mono text-sm leading-relaxed whitespace-pre-line">
                  {meeting.transcript}
                </div>
              </div>
            ) : (
              <div className="text-center py-8">
                <svg
                  className="w-12 h-12 mx-auto text-gray-400 mb-3"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M19 11a7 7 0 01-7 7m0 0a7 7 0 01-7-7m7 7v4m0 0H8m4 0h4m-4-8a3 3 0 01-3-3V5a3 3 0 116 0v6a3 3 0 01-3 3z"
                  />
                </svg>
                <p className="text-gray-500">No transcript available for this meeting</p>
                <button className="mt-2 text-flowky-secondary hover:text-blue-700 text-sm font-medium">
                  Upload Transcript
                </button>
              </div>
            )}
          </div>
        )}

        {activeTab === 'actions' && (
          <div className="space-y-4">
            {/* Pending Actions */}
            {pendingActions.length > 0 && (
              <div className="border border-border rounded-lg p-4">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Pending Actions</h3>
                <div className="space-y-3">
                  {pendingActions.map((action) => (
                    <div
                      key={action.id}
                      className="flex items-start gap-3 p-3 rounded-lg border border-orange-200 bg-orange-50/50"
                    >
                      <button
                        onClick={() => handleActionToggle(action.id)}
                        className="mt-1 w-4 h-4 border-2 border-orange-400 rounded hover:bg-orange-100 transition-colors"
                      />
                      <div className="flex-1">
                        <p className="font-medium text-gray-900">{action.task}</p>
                        <div className="flex items-center gap-4 mt-1 text-sm text-gray-600">
                          <span>👤 {action.assignee}</span>
                          <span>📅 Due {new Date(action.dueDate).toLocaleDateString()}</span>
                        </div>
                      </div>
                      <button
                        onClick={() => handleCreateTask(action)}
                        className="px-3 py-1 text-xs bg-blue-100 text-blue-700 rounded hover:bg-blue-200 transition-colors"
                      >
                        Create Task
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Completed Actions */}
            {completedActions.length > 0 && (
              <div className="border border-border rounded-lg p-4">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Completed Actions</h3>
                <div className="space-y-3">
                  {completedActions.map((action) => (
                    <div
                      key={action.id}
                      className="flex items-start gap-3 p-3 rounded-lg border border-green-200 bg-green-50/50 opacity-75"
                    >
                      <div className="mt-1 w-4 h-4 bg-green-500 rounded flex items-center justify-center">
                        <svg className="w-3 h-3 text-white" fill="currentColor" viewBox="0 0 20 20">
                          <path
                            fillRule="evenodd"
                            d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                            clipRule="evenodd"
                          />
                        </svg>
                      </div>
                      <div className="flex-1">
                        <p className="font-medium text-gray-700 line-through">{action.task}</p>
                        <div className="flex items-center gap-4 mt-1 text-sm text-gray-500">
                          <span>👤 {action.assignee}</span>
                          <span>✅ Completed</span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {!pendingActions.length && !completedActions.length && (
              <div className="border border-border rounded-lg p-4 text-center py-8">
                <svg
                  className="w-12 h-12 mx-auto text-gray-400 mb-3"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M9 5H7a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4"
                  />
                </svg>
                <p className="text-gray-500">No action items for this meeting</p>
                <button className="mt-2 text-flowky-secondary hover:text-blue-700 text-sm font-medium">
                  Add Action Item
                </button>
              </div>
            )}
          </div>
        )}

        {/* AI Insights Panel */}
        <div className="border border-border rounded-lg p-4 mt-6 border-l-4 border-purple-500">
          <div className="flex items-center gap-2 mb-3">
            <svg
              className="w-5 h-5 text-purple-600"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z"
              />
            </svg>
            <h3 className="font-semibold text-gray-900">AI Insights</h3>
          </div>
          <div className="space-y-2 text-sm">
            <p className="text-gray-700">
              • This meeting generated {meeting.actionItems?.length || 0} action items
            </p>
            <p className="text-gray-700">• {pendingActions.length} items still need attention</p>
            {linkedTasks.length > 0 && (
              <p className="text-gray-700">
                • Connected to {linkedTasks.length} existing task
                {linkedTasks.length !== 1 ? 's' : ''}
              </p>
            )}
            <p className="text-gray-700">• Next follow-up recommended within 1 week</p>
          </div>
        </div>
      </div>
    </AppLayout>
  );
}
